import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, KeyRound, Mail, RefreshCw } from 'lucide-react';
import api from '../../utils/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  // step 1=username, 2=choose method / verify, 3=new password
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState('question'); // 'question' | 'email'
  const [loading, setLoading] = useState(false);

  const [username, setUsername] = useState('');
  const [question, setQuestion] = useState('');
  const [hasQuestion, setHasQuestion] = useState(false);
  const [answer, setAnswer] = useState('');

  // Email flow state
  const [emailSent, setEmailSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');

  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!username.trim()) { toast.error('Ingresa tu nombre de usuario'); return; }
    setLoading(true);
    try {
      const res = await api.get(`/auth/forgot/question/${encodeURIComponent(username.trim())}`);
      setQuestion(res.data.question);
      setHasQuestion(!!res.data.has_question);
      // Si tiene pregunta, defaultea a método pregunta; si no, a email
      setMethod(res.data.has_question ? 'question' : 'email');
      setStep(2);
    } catch (e) {
      toast.error('No se pudo continuar. Vuelve a intentar.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyQuestion = async (e) => {
    e.preventDefault();
    if (!answer.trim()) { toast.error('Ingresa tu respuesta'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot/verify', { username: username.trim(), answer: answer.trim() });
      setResetToken(res.data.reset_token);
      toast.success('Respuesta correcta. Ahora define tu nueva contraseña.');
      setStep(3);
    } catch (e) {
      toast.error('Respuesta incorrecta. Vuelve a intentarlo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmailCode = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot/email/request', { username: username.trim() });
      if (res.data.sent) {
        setEmailSent(true);
        setMaskedEmail(res.data.masked_email || '');
        toast.success('Código enviado. Revisa tu correo (y la carpeta de spam).');
      } else {
        toast.error('Este usuario no tiene email registrado o el envío falló. Usa la pregunta de seguridad.');
      }
    } catch (e) {
      toast.error('Error al solicitar el código. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e) => {
    e.preventDefault();
    if (!emailCode.trim() || emailCode.trim().length !== 6) { toast.error('Ingresa el código de 6 dígitos'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot/email/verify', { username: username.trim(), code: emailCode.trim() });
      setResetToken(res.data.reset_token);
      toast.success('Código válido. Ahora define tu nueva contraseña.');
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Código incorrecto o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { toast.error('Las contraseñas no coinciden'); return; }
    setLoading(true);
    try {
      await api.post('/auth/forgot/reset', { reset_token: resetToken, new_password: newPassword });
      toast.success('Contraseña restablecida. Ya puedes iniciar sesión.');
      setTimeout(() => navigate('/admin/login'), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const StepDot = ({ n, active, done }) => (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border transition-all duration-300 ${
      done ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
        : active ? 'bg-white text-black border-white'
        : 'bg-transparent border-white/20 text-white/40'
    }`}>{done ? '✓' : n}</div>
  );

  const switchMethod = (m) => {
    setMethod(m);
    setAnswer('');
    setEmailCode('');
    setEmailSent(false);
    setMaskedEmail('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-10">
          <h1 className="font-['Playfair_Display'] text-3xl tracking-tighter">HAUTE</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">Recuperar acceso</p>
        </Link>

        <div className="bg-[#0A0A0A] border border-white/10 p-7 sm:p-8 rounded-lg">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-7">
            <StepDot n={1} active={step === 1} done={step > 1} />
            <div className={`h-px w-8 ${step > 1 ? 'bg-emerald-500/40' : 'bg-white/10'}`} />
            <StepDot n={2} active={step === 2} done={step > 2} />
            <div className={`h-px w-8 ${step > 2 ? 'bg-emerald-500/40' : 'bg-white/10'}`} />
            <StepDot n={3} active={step === 3} done={false} />
          </div>

          {step === 1 && (
            <form onSubmit={handleStep1} data-testid="forgot-step-1">
              <div className="text-center mb-6">
                <ShieldCheck className="w-8 h-8 mx-auto text-white/60 mb-2" />
                <h2 className="text-lg font-semibold">Identifica tu cuenta</h2>
                <p className="text-xs text-white/50 mt-1">Ingresa tu nombre de usuario para continuar.</p>
              </div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Usuario</label>
              <input
                data-testid="forgot-username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                autoFocus
                className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none px-0 py-3 text-white placeholder-gray-600 transition-colors duration-300 font-light mb-6"
              />
              <button
                data-testid="forgot-step1-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-xs font-semibold disabled:opacity-50"
              >
                {loading ? 'Cargando...' : 'Continuar'}
              </button>
            </form>
          )}

          {step === 2 && (
            <div data-testid="forgot-step-2">
              {/* Method switcher */}
              <div className="grid grid-cols-2 gap-1 bg-[#121212] border border-white/10 p-1 rounded-md mb-6">
                <button
                  type="button"
                  onClick={() => switchMethod('question')}
                  disabled={!hasQuestion}
                  data-testid="method-question"
                  className={`flex items-center justify-center gap-1.5 py-2 text-[10px] uppercase tracking-wider rounded transition-colors ${
                    method === 'question' ? 'bg-white text-black' : 'text-white/50 hover:text-white'
                  } ${!hasQuestion ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  Pregunta
                </button>
                <button
                  type="button"
                  onClick={() => switchMethod('email')}
                  data-testid="method-email"
                  className={`flex items-center justify-center gap-1.5 py-2 text-[10px] uppercase tracking-wider rounded transition-colors ${
                    method === 'email' ? 'bg-white text-black' : 'text-white/50 hover:text-white'
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" />
                  Correo
                </button>
              </div>

              {/* Security question method */}
              {method === 'question' && (
                <form onSubmit={handleVerifyQuestion}>
                  <div className="text-center mb-6">
                    <KeyRound className="w-8 h-8 mx-auto text-white/60 mb-2" />
                    <h2 className="text-lg font-semibold">Pregunta de seguridad</h2>
                    <p className="text-xs text-white/50 mt-1">Responde correctamente para continuar.</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-md p-3 mb-4">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Tu pregunta</p>
                    <p className="text-sm text-white">{question}</p>
                  </div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Respuesta</label>
                  <input
                    data-testid="forgot-answer"
                    type="password"
                    value={answer}
                    onChange={e => setAnswer(e.target.value)}
                    required
                    placeholder="Tu respuesta"
                    className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none px-0 py-3 text-white placeholder-gray-600 transition-colors duration-300 font-light mb-6"
                  />
                  <p className="text-[11px] text-white/40 mb-5">
                    La respuesta no distingue mayúsculas ni espacios alrededor.
                  </p>
                  <button
                    data-testid="forgot-step2-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-xs font-semibold disabled:opacity-50"
                  >
                    {loading ? 'Verificando...' : 'Verificar respuesta'}
                  </button>
                </form>
              )}

              {/* Email method */}
              {method === 'email' && (
                <form onSubmit={handleVerifyEmailCode}>
                  <div className="text-center mb-6">
                    <Mail className="w-8 h-8 mx-auto text-white/60 mb-2" />
                    <h2 className="text-lg font-semibold">Recuperar por correo</h2>
                    <p className="text-xs text-white/50 mt-1">Enviaremos un código de 6 dígitos al correo registrado.</p>
                  </div>

                  {!emailSent ? (
                    <button
                      type="button"
                      onClick={handleRequestEmailCode}
                      disabled={loading}
                      data-testid="forgot-request-email-code"
                      className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-xs font-semibold disabled:opacity-50"
                    >
                      {loading ? 'Enviando...' : 'Enviar código a mi correo'}
                    </button>
                  ) : (
                    <>
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 mb-5 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Código enviado a</p>
                        <p className="text-sm text-white font-mono">{maskedEmail || 'tu correo'}</p>
                        <p className="text-[11px] text-white/50 mt-2">Válido por 10 minutos. Revisa spam si no lo encuentras.</p>
                      </div>
                      <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Código de 6 dígitos</label>
                      <input
                        data-testid="forgot-email-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={emailCode}
                        onChange={e => setEmailCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        required
                        className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none px-0 py-3 text-white placeholder-gray-600 font-mono text-2xl tracking-[0.5em] text-center mb-6"
                      />
                      <button
                        data-testid="forgot-verify-email-code"
                        type="submit"
                        disabled={loading}
                        className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-xs font-semibold disabled:opacity-50 mb-3"
                      >
                        {loading ? 'Verificando...' : 'Verificar código'}
                      </button>
                      <button
                        type="button"
                        onClick={handleRequestEmailCode}
                        disabled={loading}
                        data-testid="forgot-resend-email-code"
                        className="w-full text-xs text-white/50 hover:text-white flex items-center justify-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" /> Reenviar código
                      </button>
                    </>
                  )}
                </form>
              )}

              <button type="button" onClick={() => { setStep(1); setAnswer(''); setEmailCode(''); setEmailSent(false); }} className="w-full mt-5 text-xs text-white/50 hover:text-white">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> Cambiar de usuario
              </button>
            </div>
          )}

          {step === 3 && (
            <form onSubmit={handleStep3} data-testid="forgot-step-3">
              <div className="text-center mb-6">
                <ShieldCheck className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                <h2 className="text-lg font-semibold">Nueva contraseña</h2>
                <p className="text-xs text-white/50 mt-1">Define una contraseña nueva para tu cuenta.</p>
              </div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Nueva contraseña</label>
              <input
                data-testid="forgot-new-password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                placeholder="Mínimo 6 caracteres"
                className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none px-0 py-3 text-white placeholder-gray-600 transition-colors duration-300 font-light mb-5"
              />
              <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Confirmar contraseña</label>
              <input
                data-testid="forgot-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                placeholder="Repite la contraseña"
                className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none px-0 py-3 text-white placeholder-gray-600 transition-colors duration-300 font-light mb-6"
              />
              <button
                data-testid="forgot-step3-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-xs font-semibold disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Restablecer contraseña'}
              </button>
            </form>
          )}

          <p className="text-xs text-center text-white/60 mt-6">
            <Link to="/admin/login" className="text-white underline hover:text-white/80">
              Volver al inicio de sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
